import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import inventoryService from "../../../api/inventoryService";

const MovementsHistory = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [page, setPage] = useState(1);
  const [filterTechnician, setFilterTechnician] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  useEffect(() => {
    loadMovements();
  }, [filterType, filterTechnician, filterProduct, page]);

  const loadMovements = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        ...(filterType !== "all" && { type: filterType }),
        ...(filterTechnician !== "all" && { technicianId: filterTechnician }),
        ...(filterProduct !== "all" && { itemId: filterProduct }),
      };
      const res = await inventoryService.getMovements(params);
      setMovements(res.data.data || []);
      setPagination(res.data.pagination || {
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 1,
      });
    } catch (err) {
      console.error("Error cargando movimientos:", err);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  };

  const technicians = Array.from(
    new Map(
      movements
        .flatMap((m) => [m.fromLocationId, m.toLocationId])
        .filter((loc) => loc?.type === "technician")
        .map((loc) => [loc.technicianId, loc])
    ).values()
  );

  const products = Array.from(
    new Map(
      movements
        .filter((m) => m.itemId)
        .map((m) => [m.itemId._id, m.itemId])
    ).values()
  );

  const filteredMovements = movements.filter((m) => {
    const technicianMatch =
      filterTechnician === "all" ||
      [m.fromLocationId, m.toLocationId].some(
        (l) => l?.technicianId === filterTechnician
      );

    const productMatch =
      filterProduct === "all" || m.itemId?._id === filterProduct;

    return technicianMatch && productMatch;
  });

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setPage(1); // Reset to first page when filter changes
  };

  const getMovementTypeLabel = (type) => {
    const labels = {
      transfer: "Transferencia",
      consumption: "Consumo",
      return: "Devolución",
      adjustment: "Ajuste",
    };
    return labels[type] || type;
  };

  const getMovementTypeColor = (type) => {
    const colors = {
      transfer: "primary",
      consumption: "error",
      return: "success",
      adjustment: "warning",
    };
    return colors[type] || "default";
  };

  const getLocationLabel = (location) => {
    if (!location) return "N/A";
    
    if (location.type === "main_warehouse") {
      // Usar el nombre específico del almacén o fallback a "Almacén Principal"
      return location.name || "Almacén Principal";
    }
    if (location.type === "dangerous_goods") {
      return location.name || "Residuos Peligrosos";
    }
    if (location.type === "technician") {
      return location.technicianName || `Técnico ${location.technicianId}`;
    }
    return location.name || "Ubicación desconocida";
  };

  const getLocationIcon = (location) => {
    if (!location) return null;
    
    if (location.type === "main_warehouse") return <WarehouseIcon fontSize="small" />;
    if (location.type === "dangerous_goods") return <DeleteForeverIcon fontSize="small" />;
    if (location.type === "technician") return <EngineeringIcon fontSize="small" />;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          py: 8,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Cargando historial...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (movements.length === 0 && filterType === "all" && filterTechnician === "all" && filterProduct === "all" ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          py: 8,
        }}
      >
        <InventoryIcon sx={{ fontSize: 120, color: "grey.300", mb: 3 }} />
        <Typography variant="h5" color="text.secondary" fontWeight={700} mb={1}>
          No hay movimientos registrados
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Los movimientos de inventario aparecerán aquí
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Filtros e información de paginación */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Mostrando {movements.length} de {pagination.total} movimientos
        </Typography>

        {/* Tipo */}
        <Box sx={{
          display: "flex",
          gap: 2,
        }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filterType}
              label="Tipo"
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="transfer">Transferencias</MenuItem>
              <MenuItem value="consumption">Consumos</MenuItem>
              <MenuItem value="return">Devoluciones</MenuItem>
              <MenuItem value="adjustment">Ajustes</MenuItem>
            </Select>
          </FormControl>

          {/* Técnico */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Técnico</InputLabel>
            <Select
              value={filterTechnician}
              label="Técnico"
              onChange={(e) => {
                setFilterTechnician(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              {technicians.map((tech) => (
                <MenuItem key={tech.technicianId} value={tech.technicianId}>
                  {tech.technicianName || `Técnico ${tech.technicianId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Producto */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Producto</InputLabel>
            <Select
              value={filterProduct}
              label="Producto"
              onChange={(e) => {
                setFilterProduct(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              {products.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tabla de movimientos */}
      <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto", mb: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Artículo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cantidad</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Desde</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Hacia</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Razón</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMovements.map((movement) => (
              <TableRow key={movement._id} hover>
                <TableCell>{formatDate(movement.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={getMovementTypeLabel(movement.type)}
                    color={getMovementTypeColor(movement.type)}
                    size="small"
                    icon={
                      movement.type === "transfer" ? (
                        <SwapHorizIcon />
                      ) : movement.type === "consumption" ? (
                        <DeleteOutlineIcon />
                      ) : null
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {movement.itemId?.name || "N/A"}
                  </Typography>
                  {movement.itemId?.sku && (
                    <Typography variant="caption" color="text.secondary">
                      SKU: {movement.itemId.sku}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {movement.quantity} {movement.itemId?.unit || "u"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {movement.fromLocationId ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {getLocationIcon(movement.fromLocationId)}
                      <Typography variant="body2">
                        {getLocationLabel(movement.fromLocationId)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {movement.toLocationId ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {getLocationIcon(movement.toLocationId)}
                      <Typography variant="body2">
                        {getLocationLabel(movement.toLocationId)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {movement.type === "consumption" ? "Consumido" : "N/A"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {movement.reason || "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default MovementsHistory;

